import React, { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  Button,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useNavigationStack } from '../../../../context/NavigationStackContext';
import { useContacts } from '../../hooks/useContacts';
import { usePermissions } from '../../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../../hooks/mui/useConfirmDialog';

import PageHeader from '../../../../components/common/PageHeader';

const TYPE_COLORS = {
  person: 'info',
  company: 'primary',
  authority: 'warning',
  vendor: 'default',
};

export default function ContactDetailsPage() {
  const { t } = useTranslation(['contacts', 'common']);
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const permissions = usePermissions();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { currentContact, loading, fetchContactById, deleteContact } = useContacts();

  useEffect(() => {
    fetchContactById(contactId);
  }, [contactId]);

  const canUpdate = permissions.hasScope('contact:update');
  const canDelete = permissions.hasScope('contact:delete');

  const handleDelete = useCallback(() => {
    if (!currentContact) return;
    showConfirmDialog({
      title: t('contacts:confirm.delete_title'),
      message: t('contacts:confirm.delete_message', { name: currentContact.name }),
      confirmText: t('common:buttons.delete'),
      cancelText: t('common:buttons.cancel'),
      severity: 'error',
      onConfirm: async () => {
        const result = await deleteContact(contactId);
        if (result.success) {
          showSuccess(t('contacts:success.deleted'));
          navigate('/contacts');
        } else {
          showError(result.error || t('contacts:errors.delete_failed'));
        }
      },
    });
  }, [showConfirmDialog, deleteContact, contactId, currentContact, showSuccess, showError, navigate, t]);

  if (loading || !currentContact) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress size={48} />
      </Box>
    );
  }

  const c = currentContact;

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <PageHeader
        onBack={() => navigateBack('/contacts')}
        title={c.name}
        badges={<>
          <Chip label={t(`contacts:types.${c.contact_type}`)} color={TYPE_COLORS[c.contact_type] || 'default'} size="small" />
          {!c.is_active && <Chip label={t('common:status.inactive')} color="default" size="small" />}
        </>}
        subtitle={c.att}
        actions={<>
          {canDelete && (
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
              {t('common:buttons.delete')}
            </Button>
          )}
          {canUpdate && (
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/contacts/${contactId}/edit`)}>
              {t('common:buttons.edit')}
            </Button>
          )}
        </>}
      />

      <Box display="flex" flexDirection="column" gap={2}>
        {/* Basic info */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>{t('contacts:sections.basic_info')}</Typography>
            <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={2}>
              {c.role && (
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('contacts:fields.role')}</Typography>
                  <Typography>{c.role}</Typography>
                </Box>
              )}
              {c.cvr_number && (
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('contacts:fields.cvr_number')}</Typography>
                  <Typography>{c.cvr_number}</Typography>
                </Box>
              )}
              {c.website && (
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('contacts:fields.website')}</Typography>
                  <Typography>
                    <a href={c.website} target="_blank" rel="noopener noreferrer">{c.website}</a>
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Phone numbers */}
        {c.phone_numbers?.length > 0 && (
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PhoneIcon fontSize="small" />
                <Typography variant="subtitle1" fontWeight={600}>{t('contacts:fields.phone_numbers')}</Typography>
              </Box>
              <List dense disablePadding>
                {c.phone_numbers.map((p, idx) => (
                  <ListItem key={p.phone_id} disablePadding sx={{ py: 0.25 }}>
                    <ListItemText
                      primary={`${p.country_code} ${p.number}`}
                      secondary={p.label || (idx === 0 ? t('contacts:labels.primary') : null)}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Emails */}
        {c.emails?.length > 0 && (
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <EmailIcon fontSize="small" />
                <Typography variant="subtitle1" fontWeight={600}>{t('contacts:fields.emails')}</Typography>
              </Box>
              <List dense disablePadding>
                {c.emails.map((e, idx) => (
                  <ListItem key={e.email_id} disablePadding sx={{ py: 0.25 }}>
                    <ListItemText
                      primary={<a href={`mailto:${e.email}`}>{e.email}</a>}
                      secondary={e.label || (idx === 0 ? t('contacts:labels.primary') : null)}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Address */}
        {c.address && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>{t('contacts:sections.address')}</Typography>
              <Typography>{c.address.address_line1}</Typography>
              {c.address.address_line2 && <Typography>{c.address.address_line2}</Typography>}
              <Typography>{c.address.postal_code} {c.address.city}</Typography>
              <Typography>{c.address.country}</Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      <ConfirmDialog />
    </Box>
  );
}
